import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, User, Mail, Trash2, Shield, Edit } from 'lucide-react';
import { toast } from 'sonner';

const Administracao = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditPermissionsOpen, setIsEditPermissionsOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    password: '',
    tipo: 'Estagiário',
    permissoes: {
      ver_clientes: true,
      editar_clientes: false,
      ver_processos: true,
      editar_processos: false,
      ver_audiencias: true,
      editar_audiencias: false,
      ver_atendimentos: true,
      editar_atendimentos: false,
      ver_orcamentos: true,
      editar_orcamentos: false,
      ver_anotacoes: true,
      editar_anotacoes: false,
      ver_financeiro: false,
      editar_financeiro: false,
      admin: false
    }
  });
  const [editPermissions, setEditPermissions] = useState({});

  useEffect(() => {
    loadUsuarios();
  }, []);

  const loadUsuarios = async () => {
    try {
      const response = await api.getUsuarios();
      setUsuarios(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validação básica no frontend
    if (!formData.nome || !formData.email || !formData.password) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    // Validar email básico
    if (!formData.email.includes('@') || !formData.email.includes('.')) {
      toast.error('Digite um email válido (ex: usuario@empresa.com)');
      return;
    }
    
    try {
      console.log('Enviando dados:', formData);
      const response = await api.createUsuario(formData);
      console.log('Resposta:', response.data);
      toast.success('Usuário criado com sucesso!');
      setIsDialogOpen(false);
      setFormData({
        nome: '',
        email: '',
        password: '',
        tipo: 'Estagiário',
        permissoes: {
          ver_clientes: true,
          editar_clientes: false,
          ver_processos: true,
          editar_processos: false,
          ver_audiencias: true,
          editar_audiencias: false,
          ver_atendimentos: true,
          editar_atendimentos: false,
          ver_orcamentos: true,
          editar_orcamentos: false,
          ver_anotacoes: true,
          editar_anotacoes: false,
          ver_financeiro: false,
          editar_financeiro: false,
          admin: false
        }
      });
      loadUsuarios();
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      console.error('Detalhes:', error.response?.data);
      
      // Tratar diferentes tipos de erro
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Erro de validação do Pydantic
          const msg = error.response.data.detail[0]?.msg || 'Erro de validação';
          if (msg.includes('email')) {
            toast.error('Email inválido. Use o formato: usuario@empresa.com');
          } else {
            toast.error(msg);
          }
        } else {
          toast.error(error.response.data.detail);
        }
      } else {
        toast.error('Erro ao criar usuário. Verifique os dados e tente novamente.');
      }
    }
  };

  const handleEditPermissions = (usuario) => {
    setEditingUser(usuario);
    setEditPermissions(usuario.permissoes || {
      ver_clientes: false,
      editar_clientes: false,
      ver_processos: false,
      editar_processos: false,
      ver_audiencias: false,
      editar_audiencias: false,
      ver_atendimentos: false,
      editar_atendimentos: false,
      ver_orcamentos: false,
      editar_orcamentos: false,
      ver_anotacoes: false,
      editar_anotacoes: false,
      ver_financeiro: false,
      editar_financeiro: false,
      admin: false
    });
    setIsEditPermissionsOpen(true);
  };

  const handleSavePermissions = async () => {
    try {
      await api.updateUsuario(editingUser.id, { permissoes: editPermissions });
      toast.success('Permissões atualizadas com sucesso!');
      setIsEditPermissionsOpen(false);
      setEditingUser(null);
      loadUsuarios();
    } catch (error) {
      console.error('Erro ao atualizar permissões:', error);
      toast.error('Erro ao atualizar permissões');
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`⚠️ ATENÇÃO!\n\nDeseja EXCLUIR PERMANENTEMENTE o usuário "${userName}"?\n\nEsta ação não pode ser desfeita!`)) return;
    try {
      await api.deleteUsuario(userId);
      toast.success('Usuário excluído com sucesso!');
      loadUsuarios();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const handlePermissaoChange = (permissao, checked) => {
    setFormData(prev => ({
      ...prev,
      permissoes: {
        ...prev.permissoes,
        [permissao]: checked
      }
    }));
  };

  const handleEditPermissaoChange = (permissao, checked) => {
    setEditPermissions(prev => ({
      ...prev,
      [permissao]: checked
    }));
  };

  const permissoesConfig = [
    { key: 'ver_clientes', label: 'Ver Clientes' },
    { key: 'editar_clientes', label: 'Editar Clientes' },
    { key: 'ver_processos', label: 'Ver Processos' },
    { key: 'editar_processos', label: 'Editar Processos' },
    { key: 'ver_audiencias', label: 'Ver Audiências' },
    { key: 'editar_audiencias', label: 'Editar Audiências' },
    { key: 'ver_atendimentos', label: 'Ver Atendimentos' },
    { key: 'editar_atendimentos', label: 'Editar Atendimentos' },
    { key: 'ver_orcamentos', label: 'Ver Orçamentos' },
    { key: 'editar_orcamentos', label: 'Editar Orçamentos' },
    { key: 'ver_anotacoes', label: 'Ver Anotações' },
    { key: 'editar_anotacoes', label: 'Editar Anotações' },
    { key: 'ver_financeiro', label: 'Ver Financeiro' },
    { key: 'editar_financeiro', label: 'Editar Financeiro' },
    { key: 'admin', label: 'Administrador' }
  ];

  const renderPermissoesCheckboxes = (permissoes, onChange) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-80 overflow-y-auto p-2 border rounded-md">
      {permissoesConfig.map(({ key, label }) => (
        <label key={key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
          <input
            type="checkbox"
            checked={permissoes[key] || false}
            onChange={(e) => onChange(key, e.target.checked)}
            className="rounded border-slate-300"
          />
          <span className={key === 'admin' ? 'font-bold text-red-600' : ''}>
            {label}
          </span>
        </label>
      ))}
    </div>
  );

  if (loading) {
    return <div className="p-4 sm:p-8" data-testid="admin-loading">Carregando...</div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Administração</h1>
          <p className="text-slate-600 mt-1 text-sm sm:text-base">Gerencie usuários e permissões</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="add-usuario-button">
              <Plus className="w-4 h-4 mr-2" />
              Novo Usuário
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Cadastrar Novo Usuário</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="nome">Nome Completo *</Label>
                <Input
                  id="nome"
                  required
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  data-testid="usuario-nome-input"
                />
              </div>
              <div>
                <Label htmlFor="email">E-mail *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="usuario-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password">Senha *</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  data-testid="usuario-password-input"
                />
              </div>
              <div>
                <Label htmlFor="tipo">Tipo de Usuário</Label>
                <Select
                  value={formData.tipo}
                  onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                >
                  <SelectTrigger data-testid="usuario-tipo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Advogado">Advogado</SelectItem>
                    <SelectItem value="Estagiário">Estagiário</SelectItem>
                    <SelectItem value="Secretário">Secretário(a)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Permissões</Label>
                {renderPermissoesCheckboxes(formData.permissoes, handlePermissaoChange)}
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-usuario-button">
                  Cadastrar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Modal de Edição de Permissões */}
      <Dialog open={isEditPermissionsOpen} onOpenChange={setIsEditPermissionsOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Permissões - {editingUser?.nome}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">Usuário: <span className="font-medium">{editingUser?.nome}</span></p>
              <p className="text-sm text-slate-600">Email: <span className="font-medium">{editingUser?.email}</span></p>
              <p className="text-sm text-slate-600">Tipo: <span className="font-medium">{editingUser?.tipo}</span></p>
            </div>
            <div>
              <Label>Permissões</Label>
              {renderPermissoesCheckboxes(editPermissions, handleEditPermissaoChange)}
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsEditPermissionsOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSavePermissions} 
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="save-permissions-button"
              >
                Salvar Permissões
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {usuarios.map((usuario) => (
          <Card key={usuario.id} className="hover:shadow-lg transition-shadow" data-testid="usuario-card">
            <CardHeader className="p-4 sm:p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                  {usuario.permissoes?.admin ? (
                    <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                  ) : (
                    <User className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-base sm:text-lg truncate">{usuario.nome}</CardTitle>
                  <span className={`inline-block mt-1 text-xs px-2 py-1 rounded ${
                    usuario.permissoes?.admin ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'
                  }`}>
                    {usuario.tipo} {usuario.permissoes?.admin && '(Admin)'}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 p-4 sm:p-6 pt-0">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{usuario.email}</span>
              </div>
              
              {/* Resumo das permissões */}
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs text-slate-500 mb-2">Permissões:</p>
                <div className="flex flex-wrap gap-1">
                  {usuario.permissoes?.ver_clientes && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Clientes</span>}
                  {usuario.permissoes?.ver_processos && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Processos</span>}
                  {usuario.permissoes?.ver_audiencias && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Audiências</span>}
                  {usuario.permissoes?.ver_atendimentos && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Atendimentos</span>}
                  {usuario.permissoes?.ver_orcamentos && <span className="text-xs bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded">Orçamentos</span>}
                  {usuario.permissoes?.ver_financeiro && <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Financeiro</span>}
                  {usuario.permissoes?.ver_anotacoes && <span className="text-xs bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded">Anotações</span>}
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {usuario.permissoes?.editar_clientes && <span className="text-xs bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded">✏️ Clientes</span>}
                  {usuario.permissoes?.editar_processos && <span className="text-xs bg-green-200 text-green-800 px-1.5 py-0.5 rounded">✏️ Processos</span>}
                  {usuario.permissoes?.editar_audiencias && <span className="text-xs bg-purple-200 text-purple-800 px-1.5 py-0.5 rounded">✏️ Audiências</span>}
                  {usuario.permissoes?.editar_atendimentos && <span className="text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">✏️ Atendimentos</span>}
                  {usuario.permissoes?.editar_orcamentos && <span className="text-xs bg-cyan-200 text-cyan-800 px-1.5 py-0.5 rounded">✏️ Orçamentos</span>}
                  {usuario.permissoes?.editar_financeiro && <span className="text-xs bg-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded">✏️ Financeiro</span>}
                  {usuario.permissoes?.editar_anotacoes && <span className="text-xs bg-pink-200 text-pink-800 px-1.5 py-0.5 rounded">✏️ Anotações</span>}
                </div>
              </div>

              <div className="flex gap-2 mt-3 pt-3 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleEditPermissions(usuario)}
                  data-testid="edit-permissions-button"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Permissões
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(usuario.id, usuario.nome)}
                  data-testid="delete-usuario-button"
                  title="Excluir usuário permanentemente"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {usuarios.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          Nenhum usuário cadastrado
        </div>
      )}
    </div>
  );
};

export default Administracao;
